import type {
  ChecklistItemResult,
  WorkflowRunStatus,
  NotificationType,
  DocumentType,
  ProjectPermission,
  ProjectRole,
  ProjectAccessStatus,
  WorkflowRunResponse,
  ChecklistResult,
  WorkflowId,
  UserRole,
  Permission,
  ProjectStatus,
  ExceptionReviewStatus,
  ValidationSummary,
  RequirementSetId,
} from 'epd-validator-shared';
export * from 'epd-validator-shared';

/**
 * Document attached to a validation
 */
export interface ValidationDocument {
  id: string;
  type: DocumentType;
  originalFilename: string;
  mimeType: string;
  uploadedAt: string;
}

/**
 * Event types used throughout the application
 */
export enum EventType {
  // Comment related events
  COMMENT_CREATED = 'COMMENT_CREATED',
  COMMENT_UPDATED = 'COMMENT_UPDATED',
  COMMENT_DELETED = 'COMMENT_DELETED',
  COMMENT_READ = 'COMMENT_READ',

  // Notification related events
  NOTIFICATION_READ = 'NOTIFICATION_READ',
  BATCH_NOTIFICATIONS_READ = 'BATCH_NOTIFICATIONS_READ',
  NOTIFICATION_CLICKED = 'NOTIFICATION_CLICKED',
  NOTIFICATION_NAVIGATION = 'NOTIFICATION_NAVIGATION',
  NOTIFICATION_CREATED = 'NOTIFICATION_CREATED',

  // Workflow run events
  WORKFLOW_RUN_UPDATED = 'WORKFLOW_RUN_UPDATED',

  // Project related events
  PROJECT_STATUS_CHANGED = 'PROJECT_STATUS_CHANGED',

  // Audit log events
  AUDIT_LOG_ENTRY_CREATED = 'AUDIT_LOG_ENTRY_CREATED',
}

/**
 * Event payload map for type-safe event payloads
 */
import type { AuditLogEntry } from './types/audit.types';
export interface EventPayloadMap {
  // Comment events
  [EventType.COMMENT_CREATED]: PopulatedComment;
  [EventType.COMMENT_UPDATED]: PopulatedComment;
  [EventType.COMMENT_DELETED]: {
    commentId: string;
    projectId: string;
    validationId: string;
    checklistItemNumber?: string;
    user?: { id: string; name: string; email: string; createdAt?: string; updatedAt?: string };
  };
  [EventType.COMMENT_READ]: {
    commentId: string;
  };

  // Notification events
  [EventType.NOTIFICATION_READ]: {
    notificationId: string;
  };
  [EventType.BATCH_NOTIFICATIONS_READ]: {
    notificationIds: string[];
  };
  [EventType.NOTIFICATION_CLICKED]: {
    notificationId: string;
  };
  [EventType.NOTIFICATION_NAVIGATION]: {
    notificationId: string;
    targetUrl: string;
  };

  // Workflow run events
  [EventType.WORKFLOW_RUN_UPDATED]: WorkflowRun;

  // Audit log events
  [EventType.AUDIT_LOG_ENTRY_CREATED]: AuditLogEntry;
}

/**
 * Event structure used in the application
 */
export interface Event<T extends EventType = EventType> {
  type: T;
  producer: string;
  payload: EventPayloadMap[T extends keyof EventPayloadMap ? T : never];
  queuedAt?: string;
}

/**
 * Type for the reasoning field in checklist items
 */
export interface Reasoning {
  explanation: string;
  page_numbers: number[];
}

export interface User {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  permissions?: Permission[];
  createdAt: string;
  updatedAt: string;
}

/**
 * Registration input
 */
export interface RegisterInput {
  email: string;
  name: string;
}

/**
 * Login code request input
 */
export interface RequestLoginCodeInput {
  email: string;
}

/**
 * Login code verification input
 */
export interface VerifyLoginCodeInput {
  email: string;
  code: string;
}

/**
 * Profile update input
 */
export interface UpdateProfileInput {
  name: string;
}

/**
 * Information about an active validation exception (override)
 */
export interface ValidationExceptionInfo {
  overrideId: string; // The ID of the ValidationOverride document
  userId: string; // Creator ID
  userDisplayName?: string; // Name of the creator
  comment: string;
  createdAt: string; // ISO date string
  updatedAt: string; // ISO date string
  lastUpdatedBy?: string; // Last editor ID
  lastUpdatedByDisplayName?: string; // Name of the last editor
  originalStatus: ChecklistItemResult;
  reviewStatus: ExceptionReviewStatus;
  reviewedBy?: string; // Reviewer ID
  reviewedByDisplayName?: string; // Name of the reviewer
  reviewedAt?: string; // ISO date string
  reviewComment?: string; // Comment from reviewer (for both approval and rejection)
}

/**
 * Represents a full validation override record, typically including populated user details.
 */
export interface ValidationOverride {
  id: string;
  projectId: string;
  checklistItemId: string; // Corresponds to ChecklistItem.number
  userId: string; // ID of the user who created the override
  comment: string;
  originalStatus: ChecklistItemResult;
  overrideStatus: ChecklistItemResult; // The status after override
  lastUpdatedBy?: string; // ID of the user who last updated the override
  reviewStatus: ExceptionReviewStatus;
  reviewedBy?: string; // ID of the reviewer
  reviewedAt?: string; // ISO date string
  reviewComment?: string; // Comment from reviewer (for both approval and rejection)
  createdAt: string; // ISO date string
  updatedAt: string; // ISO date string
  user: User; // Populated information of the user who created the override
  lastUpdatedByUser?: User; // Populated information of the user who last updated
  reviewedByUser?: User; // Populated information of the reviewer
}

/**
 * Checklist item in validation results
 */
export type ChecklistItem = ChecklistResult;

/**
 * Represents a ChecklistItem after client-side processing,
 * potentially including merged exception information.
 */
export interface ChecklistItemWithOverrides extends ChecklistResult {
  exception?: ValidationExceptionInfo;
}

/**
 * Owner information
 */
export interface Owner {
  id: string;
  name: string;
  email: string;
}

/**
 * Workflow run interface matching backend response
 */
export interface WorkflowRun<T extends WorkflowId = WorkflowId> {
  id: string;
  projectId: string;
  workflowId: WorkflowId;
  name: string;
  status: WorkflowRunStatus;
  runId?: string;
  runInfo?: WorkflowRunResponse<T>;
  error?: {
    message: string;
  };
  createdAt: string;
  updatedAt: string;
  completedAt?: string;
  lastPolledAt?: string;
}

/**
 * Comment interface for checklist item comments
 */
export interface Comment {
  id: string;
  projectId: string;
  validationId: string;
  checklistItemNumber: string;
  userId: string;
  content: string;
  createdAt: string;
  updatedAt: string;
  user?: {
    id: string;
    name: string;
    email: string;
    createdAt?: string;
    updatedAt?: string;
  };
  mentions?: {
    id: string;
    name: string;
    email: string;
  }[];
}

/**
 * Notification interface
 */
export interface Notification {
  id: string;
  type: NotificationType;
  isRead: boolean;
  createdAt: string;
  /**
   * ID of the user who should receive the notification
   */
  userId: string;
  /**
   * Standardized notification data structure
   */
  data?: {
    /**
     * Title of the notification
     */
    title?: string;
    /**
     * Message content of the notification
     */
    message?: string;
    /**
     * ID of the related entity (validation, comment, etc.)
     */
    relatedId?: string;
    /**
     * Type of the related entity
     */
    relatedType?: string;
    /**
     * ID of the user who triggered the notification
     */
    fromUserId?: string;
    /**
     * Current status of the invitation (for invitation notifications)
     */
    invitationStatus?: ProjectAccessStatus;
    /**
     * ID of the project related to this notification
     */
    projectId?: string;
    /**
     * List of user IDs mentioned in the comment (for comment notifications)
     */
    mentionedUserIds?: string[];
  };

  /**
   * Populated entities - these are set by the backend when populate parameter is used
   */
  comment?: Comment;
  inviter?: {
    id: string;
    name: string;
    email?: string;
  };
  validation?: WorkflowRun;
}

// Supported event stream types
export type StreamEvent =
  | { type: EventType.NOTIFICATION_CREATED; data: Notification }
  | { type: EventType.WORKFLOW_RUN_UPDATED; data: WorkflowRun }
  | { type: EventType.COMMENT_CREATED; data: EventPayloadMap[EventType.COMMENT_CREATED] }
  | { type: EventType.COMMENT_UPDATED; data: EventPayloadMap[EventType.COMMENT_UPDATED] }
  | { type: EventType.COMMENT_DELETED; data: EventPayloadMap[EventType.COMMENT_DELETED] }
  | { type: EventType.PROJECT_STATUS_CHANGED; data: { projectId: string; status: ProjectStatus } }
  | { type: EventType.AUDIT_LOG_ENTRY_CREATED; data: EventPayloadMap[EventType.AUDIT_LOG_ENTRY_CREATED] };

export interface Project {
  id: string;
  name: string;
  status: ProjectStatus;
  requirementSetId: RequirementSetId;
  epdDocument?: ValidationDocument;
  lcaDocument?: ValidationDocument;
  additionalDocuments?: ValidationDocument[];
  createdAt: string;
  updatedAt: string;
  user?: User;
  userPermissions: ProjectPermission[];
  userRole?: ProjectRole;
  latestValidationRun?: WorkflowRun<WorkflowId.EPD_VALIDATION>;
  latestMatchingRun?: WorkflowRun<WorkflowId.EPD_MATCHING>;
  validationSummary?: ValidationSummary;
}

export interface ProjectAccess {
  id: string;
  projectId: string;
  userId: string;
  user?: {
    id: string;
    name: string;
    email: string;
  };
  role: ProjectRole;
}

// Add PopulatedComment type to match backend IPopulatedComment
export interface PopulatedComment extends Comment {
  user?: {
    id: string;
    name: string;
    email: string;
    createdAt?: string;
    updatedAt?: string;
  };
  validationId: string;
  parentComment?: PopulatedComment;
}
